import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  DialogActions,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  RowTableCell,
} from '../../../common';
import { useReceiveStockTransfer } from '../../../hooks/useStockTransfers';
import {
  buildReceiveTransferPayload,
  getArticleLabel,
  getInternalOrderApiErrorMessage,
} from '../utils/internalOrdersUtils';
import { useInventoryArticlesByUuids } from '../hooks/useInventoryArticlesByUuids';

const ReceiveStockTransferDialog = ({
  open,
  onClose,
  transfer,
  onSuccess,
}) => {
  const [quantitiesByItem, setQuantitiesByItem] = useState({});
  const [errorMessage, setErrorMessage] = useState('');
  const receiveMutation = useReceiveStockTransfer();

  const articleUuids = useMemo(
    () => (transfer?.items || []).map((item) => item.uuid_inventory_article),
    [transfer?.items]
  );

  const { articles: inventoryArticles } = useInventoryArticlesByUuids(
    articleUuids,
    open && Boolean(transfer)
  );

  const articlesByUuid = useMemo(
    () =>
      inventoryArticles.reduce((acc, article) => {
        if (article?.uuid) acc[article.uuid] = article;
        return acc;
      }, {}),
    [inventoryArticles]
  );

  const pendingItems = useMemo(
    () => (transfer?.items || []).filter((item) => Number(item.pending_quantity) > 0),
    [transfer]
  );

  useEffect(() => {
    if (open && transfer) {
      const initialQuantities = pendingItems.reduce((acc, item) => {
        acc[item.uuid] = item.pending_quantity;
        return acc;
      }, {});
      setQuantitiesByItem(initialQuantities);
      setErrorMessage('');
    }
  }, [open, transfer, pendingItems]);

  const handleQuantityChange = (itemUuid, value) => {
    setQuantitiesByItem((prev) => ({
      ...prev,
      [itemUuid]: value,
    }));
    if (errorMessage) setErrorMessage('');
  };

  const handleReceive = async () => {
    if (!transfer?.uuid) return;

    const payload = buildReceiveTransferPayload(transfer, quantitiesByItem);
    if (!payload) {
      setErrorMessage('Ingresá al menos una cantidad válida para recibir.');
      return;
    }

    try {
      setErrorMessage('');
      const result = await receiveMutation.mutateAsync({
        id: transfer.uuid,
        data: payload,
      });
      onSuccess?.(result);
      onClose();
    } catch (error) {
      console.error('Error al recibir traslado:', error);
      setErrorMessage(getInternalOrderApiErrorMessage(error, 'No se pudo recibir el traslado'));
    }
  };

  if (!transfer) return null;

  return (
    <DialogContainer open={open} onClose={onClose} size="medium">
      <DialogTitle>Recibir traslado {transfer.transfer_number}</DialogTitle>

      <DialogBody>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Ingresá la cantidad recibida para cada artículo. Podés recibir total o parcialmente.
          </Typography>

          <GeneralTableBox>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <HeadTableCell>Artículo</HeadTableCell>
                  <HeadTableCell>Pendiente</HeadTableCell>
                  <HeadTableCell>Recibir</HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingItems.length === 0 ? (
                  <TableRow>
                    <RowTableCell colSpan={3} sx={{ textAlign: 'center', py: 3 }}>
                      No hay cantidades pendientes para recibir
                    </RowTableCell>
                  </TableRow>
                ) : (
                  pendingItems.map((item) => (
                    <TableRow key={item.uuid}>
                      <RowTableCell>
                        {getArticleLabel(articlesByUuid, item.uuid_inventory_article)}
                      </RowTableCell>
                      <RowTableCell>{item.pending_quantity}</RowTableCell>
                      <RowTableCell>
                        <CustomTextField
                          name={`receive-${item.uuid}`}
                          type="number"
                          value={quantitiesByItem[item.uuid] ?? ''}
                          onChange={(event) =>
                            handleQuantityChange(item.uuid, event.target.value)
                          }
                          inputProps={{
                            min: 0.001,
                            max: item.pending_quantity,
                            step: 0.001,
                          }}
                          size="small"
                          sx={{ maxWidth: 120 }}
                        />
                      </RowTableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </GeneralTableBox>
        </Box>
      </DialogBody>

      <DialogActions
        handleClose={onClose}
        handleSave={handleReceive}
        saveLabel="Recibir"
        saveButtonProps={{ isLoading: receiveMutation.isPending }}
      />
    </DialogContainer>
  );
};

export default ReceiveStockTransferDialog;
